import React, { useState, useEffect } from "react";
import { CheckCircle, WifiOff } from "lucide-react";

/** Offline/online detection hook */
function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const goOffline = () => { setOnline(false); setWasOffline(true); };
    const goOnline = () => { setOnline(true); };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  // Auto-dismiss "back online" after 4 seconds
  useEffect(() => {
    if (online && wasOffline) {
      const t = setTimeout(() => setWasOffline(false), 4000);
      return () => clearTimeout(t);
    }
  }, [online, wasOffline]);

  return { online, wasOffline };
}

export function OfflineBanner() {
  const { online, wasOffline } = useOnlineStatus();

  if (online && !wasOffline) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      padding: '8px 16px',
      background: online ? 'var(--green, #22c55e)' : 'var(--red, #ef4444)',
      color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      fontSize: 'var(--text-sm, 13px)', fontWeight: 600,
      fontFamily: 'var(--font-sans)',
      transition: 'background 0.3s ease',
    }}>
      {online ? (
        <>
          <CheckCircle size={14} />
          Back online — reconnecting...
        </>
      ) : (
        <>
          <WifiOff size={14} />
          No internet connection. Pipeline progress may be lost.
        </>
      )}
    </div>
  );
}
